import { useEffect, useId, useRef, useState, type DragEvent } from "react";
import { CaretDown, CloudArrowUp, DownloadSimple, File, Paperclip, Plus, Trash } from "@phosphor-icons/react";
import { AccountButton } from "../../shared/account/AccountUI.tsx";
import DirectionalIcon from "../../../features/navigation/DirectionalIcon.tsx";
import saveAs from "file-saver";
import { t, useStoreLanguage, getLocale } from "../../../features/store-language/i18n.ts";
import { HttpService } from "../../../utils/DesigningHttpService.js";
import AppFeedback from "../../shared/AppFeedback.tsx";
import { PendingRing } from "../../shared/LoadingRing.jsx";
import type { AccountOrderAttachment } from "./accountOrderService.ts";

export type AttachmentTask = {
  stage: "saving" | "uploading" | "attaching" | "deleting";
  fileName: string;
  attachmentId?: number;
};

type Props = {
  orderId?: number;
  attachments: AccountOrderAttachment[];
  editable: boolean;
  disabled: boolean;
  titleReady: boolean;
  task?: AttachmentTask;
  onUpload: (file: File) => Promise<void>;
  onRemove: (attachment: AccountOrderAttachment) => Promise<void>;
};

const http = new HttpService();
const maximumBytes = 10 * 1024 * 1024;
const maximumFiles = 5;
const acceptedExtensions = ["pdf", "jpg", "jpeg", "png", "webp", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "zip", "txt", "csv"];

function fileSize(value: number) {
  if (value >= 1024 * 1024) {
    return `${(value / 1024 / 1024).toLocaleString(getLocale(), { maximumFractionDigits: 1 })} ${t("مگابایت")}`;
  }
  return `${Math.max(1, value / 1024).toLocaleString(getLocale(), { maximumFractionDigits: 0 })} ${t("کیلوبایت")}`;
}

function stageLabel(stage: AttachmentTask["stage"]) {
  if (stage === "saving") return t("در حال ثبت سفارش…");
  if (stage === "uploading") return t("در حال بارگذاری فایل…");
  if (stage === "attaching") return t("در حال اتصال فایل به سفارش…");
  return t("در حال حذف پیوست…");
}

export default function OrderAttachments({
  orderId,
  attachments,
  editable,
  disabled,
  titleReady,
  task,
  onUpload,
  onRemove,
}: Props) {
  useStoreLanguage();
  const input = useRef<HTMLInputElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const previousCount = useRef(attachments.length);
  const activeDownloads = useRef(new Map<number, AbortController>());
  const [downloading, setDownloading] = useState<number[]>([]);
  const [dragging, setDragging] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const bodyId = useId();
  const uploadId = useId();
  const atCapacity = attachments.length >= maximumFiles;
  const canUpload = editable && titleReady && !disabled && !task && !atCapacity;

  useEffect(function () {
    if (attachments.length > previousCount.current) {
      setUploadOpen(false);
      toggle.current?.focus();
    }
    previousCount.current = attachments.length;
  }, [attachments.length]);

  useEffect(function () {
    const requests = activeDownloads.current;
    return function () {
      requests.forEach(function (request) { request.abort(); });
      requests.clear();
    };
  }, [orderId]);

  async function download(attachment: AccountOrderAttachment) {
    if (!orderId || activeDownloads.current.has(attachment.id)) return;
    const controller = new AbortController();
    activeDownloads.current.set(attachment.id, controller);
    setDownloading(Array.from(activeDownloads.current.keys()));
    try {
      const blob = await http.getResource(`/catalog/orders/${orderId}/attachments/${attachment.id}`,
        controller.signal, "blob");
      if (!controller.signal.aborted) {
        saveAs(blob, attachment.fileName || `order-${orderId}-attachment-${attachment.id}`);
      }
    } catch (error) {
      if (!controller.signal.aborted) AppFeedback.httpError(error);
    } finally {
      activeDownloads.current.delete(attachment.id);
      setDownloading(Array.from(activeDownloads.current.keys()));
    }
  }

  async function choose(file?: File) {
    if (!file || !canUpload) return;
    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    if (!acceptedExtensions.includes(extension)) {
      AppFeedback.error({ title: t("این نوع فایل قابل پیوست نیست.") });
      return;
    }
    if (file.size <= 0 || file.size > maximumBytes) {
      AppFeedback.error({ title: t("حجم فایل پیوست باید حداکثر ۱۰ مگابایت باشد.") });
      return;
    }
    await onUpload(file);
  }

  function drop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.files.length > 1) {
      AppFeedback.error({ title: t("هر بار یک فایل انتخاب کنید.") });
      return;
    }
    if (canUpload) void choose(event.dataTransfer.files?.[0]);
  }

  async function confirmRemove(attachment: AccountOrderAttachment) {
    if (!editable || disabled || task) return;
    const result = await AppFeedback.confirmDestructive({
      title: t("حذف پیوست"),
      text: t("پیوست «{{value0}}» از سفارش حذف شود؟", { value0: attachment.fileName }),
      confirmButtonText: t("بله، حذف شود"),
      cancelButtonText: t("انصراف"),
      showCancelButton: true,
    });
    if (result.isConfirmed) await onRemove(attachment);
  }

  return (
    <section
      className="account-order-attachments"
      aria-label={t("پیوست‌های سفارش")}
    >
      <div className="account-order-attachments__header">
        <button
          ref={toggle}
          type="button"
          className="account-order-attachments__toggle"
          aria-controls={bodyId}
          aria-expanded={expanded}
          onClick={function () {
            setExpanded(!expanded);
            if (expanded) setUploadOpen(false);
          }}
        >
          <Paperclip
            size={18}
            aria-hidden="true"
          />
          <span>{t("پیوست‌های سفارش")}</span>
          <span className="account-order-attachments__count">
            {t("{{value0}} از {{value1}} فایل", {
              value0: attachments.length.toLocaleString(getLocale()),
              value1: maximumFiles.toLocaleString(getLocale()),
            })}
          </span>
          {task ? <PendingRing /> : null}
          {expanded ? (
            <CaretDown
              size={18}
              aria-hidden="true"
            />
          ) : (
            <DirectionalIcon
              direction="forward"
              variant="chevron"
              size={18}
            />
          )}
        </button>
        {editable ? (
          <AccountButton
            type="text"
            icon={<Plus size={18} />}
            disabled={disabled || Boolean(task) || atCapacity}
            aria-controls={uploadOpen ? uploadId : undefined}
            aria-expanded={expanded && uploadOpen}
            onClick={function () { setExpanded(true); setUploadOpen(true); }}
          >
            {t("افزودن پیوست")}
          </AccountButton>
        ) : null}
      </div>
      <div
        id={bodyId}
        className="account-order-attachments__body"
        hidden={!expanded}
      >
        <p className="account-order-attachments__limits">
          {atCapacity ? t("حداکثر ۵ فایل می‌توانید به هر سفارش پیوست کنید.") : t("حداکثر ۵ فایل، هر فایل تا ۱۰ مگابایت")}
        </p>
        {editable && uploadOpen ? (
          <div
            id={uploadId}
            className={`account-order-attachments__dropzone${dragging ? " account-order-attachments__dropzone--active" : ""}`}
            aria-disabled={!canUpload}
            aria-busy={Boolean(task)}
            onDragEnter={function (event) { event.preventDefault(); if (canUpload) setDragging(true); }}
            onDragOver={function (event) { event.preventDefault(); }}
            onDragLeave={function (event) { if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false); }}
            onDrop={drop}
          >
            <input
              ref={input}
              type="file"
              hidden
              accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt,.csv"
              disabled={!canUpload}
              onChange={function (event) {
                void choose(event.target.files?.[0]);
                event.target.value = "";
              }}
            />
            <span
              className="account-order-attachments__upload-icon"
              aria-hidden="true"
            >
              {task ? <PendingRing /> : <CloudArrowUp size={26} />}
            </span>
            <div
              className="account-order-attachments__upload-copy"
              role="status"
              aria-live="polite"
            >
              <strong>{task ? stageLabel(task.stage) : t("فایل را انتخاب کنید یا اینجا رها کنید")}</strong>
              <span>
                {task?.fileName || (!titleReady
                  ? t("برای افزودن پیوست، ابتدا عنوان سفارش را وارد کنید.")
                  : t("PDF، تصویر، Office، ZIP، TXT یا CSV تا ۱۰ مگابایت"))}
              </span>
            </div>
            <AccountButton
              type="primary"
              disabled={!canUpload}
              loading={Boolean(task && task.stage !== "deleting")}
              onClick={function () { input.current?.click(); }}
            >
              {t("انتخاب فایل")}
            </AccountButton>
            <AccountButton
              disabled={Boolean(task)}
              onClick={function () { setUploadOpen(false); toggle.current?.focus(); }}
            >
              {t("انصراف")}
            </AccountButton>
          </div>
        ) : null}

        {attachments.length ? (
          <div className="account-order-attachments__list">
            {attachments.map(function (attachment) {
              const deleting = task?.stage === "deleting" && task.attachmentId === attachment.id;
              return (
                <article
                  className="account-order-attachments__item"
                  key={attachment.id}
                >
                  <span
                    className="account-order-attachments__file-icon"
                    aria-hidden="true"
                  >
                    <File size={22} />
                  </span>
                  <div className="account-order-attachments__file-copy">
                    <strong title={attachment.fileName}>
                      <bdi>{attachment.fileName || attachment.title}</bdi>
                    </strong>
                    <span>{fileSize(attachment.fileSize)}</span>
                  </div>
                  <div className="account-order-attachments__actions">
                    <AccountButton
                      icon={<DownloadSimple size={17} />}
                      loading={downloading.includes(attachment.id)}
                      disabled={!orderId || deleting}
                      onClick={function () { return download(attachment); }}
                    >
                      {t("دانلود")}
                    </AccountButton>
                    {editable ? (
                      <AccountButton
                        danger
                        icon={<Trash size={17} />}
                        loading={deleting}
                        disabled={disabled || Boolean(task && !deleting)}
                        onClick={function () { return confirmRemove(attachment); }}
                      >
                        {t("حذف")}
                      </AccountButton>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="account-order-attachments__empty">
            <File
              size={22}
              aria-hidden="true"
            />
            <span>{t("هنوز فایلی به این سفارش پیوست نشده است.")}</span>
          </div>
        )}
      </div>
    </section>
  );
}
